import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Fuse from 'fuse.js'
import { Search, FileText, Clock, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { DocsSearchResult, DocsPageMeta } from '@/types/docs'

const RECENT_SEARCHES_KEY = 'docs-recent-searches'
const MAX_RECENT_SEARCHES = 5

// Helper to get initial state from localStorage
function getInitialRecentSearches(): string[] {
  if (typeof window === 'undefined') return []
  try {
    const saved = localStorage.getItem(RECENT_SEARCHES_KEY)
    return saved ? JSON.parse(saved) : []
  } catch {
    return []
  }
}

interface DocsSearchProps {
  searchIndex: DocsPageMeta[]
  basePath: string
  className?: string
}

/**
 * Full-text search component for documentation
 */
export function DocsSearch({ searchIndex, basePath, className }: DocsSearchProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<string[]>(getInitialRecentSearches)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  // Initialize Fuse.js search
  const fuse = useMemo(() => {
    return new Fuse(searchIndex, {
      keys: [
        { name: 'title', weight: 2 },
        { name: 'content', weight: 1 },
      ],
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 2,
      ignoreLocation: true,
    })
  }, [searchIndex])

  // Save recent search
  const saveRecentSearch = useCallback((searchQuery: string) => {
    const trimmed = searchQuery.trim()
    if (!trimmed) return

    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s !== trimmed)
      const updated = [trimmed, ...filtered].slice(0, MAX_RECENT_SEARCHES)
      try {
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
      } catch {
        // Ignore localStorage errors
      }
      return updated
    })
  }, [])

  // Search results
  const results: DocsSearchResult[] = useMemo(() => {
    if (!query.trim()) return []

    return fuse.search(query).slice(0, 10).map((result) => {
      const match = result.matches?.[0]
      let preview = ''

      if (match && match.key === 'content' && match.indices.length > 0) {
        const start = Math.max(0, match.indices[0][0] - 30)
        const end = Math.min(result.item.content.length, match.indices[0][1] + 50)
        preview = (start > 0 ? '...' : '') +
          result.item.content.slice(start, end) +
          (end < result.item.content.length ? '...' : '')
      } else {
        preview = result.item.content.slice(0, 100) + '...'
      }

      return {
        title: result.item.title,
        section: result.item.section,
        path: result.item.path,
        preview,
        score: result.score ?? 0,
      }
    })
  }, [query, fuse])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Open search with Cmd/Ctrl + K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Handle keyboard navigation in search results
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const items = results.length > 0 ? results : (query ? [] : recentSearches.map(s => ({ path: s })))

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (results.length > 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex])
        } else if (!query && recentSearches[selectedIndex]) {
          handleQueryChange(recentSearches[selectedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        break
    }
  }

  // Handle result selection
  const handleSelect = (result: DocsSearchResult) => {
    saveRecentSearch(query)
    setIsOpen(false)
    setQuery('')
    setSelectedIndex(0)
    navigate(`${basePath}/${result.path}`)
  }

  // Handle query change with index reset
  const handleQueryChange = (newQuery: string) => {
    setQuery(newQuery)
    setSelectedIndex(0)
  }

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [isOpen])

  return (
    <>
      {/* Search trigger button */}
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors w-full',
          className
        )}
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search documentation...</span>
        <kbd className="hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-muted px-1.5 font-mono text-xs text-muted-foreground">
          <span className="text-xs">Ctrl</span>K
        </kbd>
      </button>

      {/* Search dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Search Documentation</DialogTitle>
          </DialogHeader>

          {/* Search input */}
          <div className="flex items-center gap-2 border-b px-4">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search documentation..."
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 h-12"
            />
            {query && (
              <button
                onClick={() => handleQueryChange('')}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto p-2">
            {/* Show results if query exists */}
            {query && results.length > 0 && (
              <div className="space-y-1">
                {results.map((result, index) => (
                  <button
                    key={result.path}
                    onClick={() => handleSelect(result)}
                    className={cn(
                      'flex w-full items-start gap-3 rounded-md p-3 text-left transition-colors',
                      index === selectedIndex
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-muted'
                    )}
                  >
                    <FileText className="h-4 w-4 mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{result.title}</div>
                      <div className={cn(
                        'text-xs truncate',
                        index === selectedIndex ? 'text-primary-foreground/80' : 'text-muted-foreground'
                      )}>
                        {result.section}
                      </div>
                      <div className={cn(
                        'text-sm mt-1 line-clamp-2',
                        index === selectedIndex ? 'text-primary-foreground/90' : 'text-muted-foreground'
                      )}>
                        {result.preview}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No results */}
            {query && results.length === 0 && (
              <div className="py-8 text-center text-muted-foreground">
                <p>No results found for "{query}"</p>
                <p className="text-sm mt-1">Try searching for different keywords</p>
              </div>
            )}

            {/* Recent searches when no query */}
            {!query && recentSearches.length > 0 && (
              <div>
                <div className="px-3 py-2 text-xs font-medium text-muted-foreground">
                  Recent searches
                </div>
                <div className="space-y-1">
                  {recentSearches.map((search, index) => (
                    <button
                      key={search}
                      onClick={() => handleQueryChange(search)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md p-3 text-left transition-colors',
                        index === selectedIndex
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-muted'
                      )}
                    >
                      <Clock className="h-4 w-4 shrink-0" />
                      <span>{search}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!query && recentSearches.length === 0 && (
              <div className="py-8 text-center text-muted-foreground">
                <p>Start typing to search documentation</p>
                <p className="text-sm mt-1">Use arrow keys to navigate, Enter to select</p>
              </div>
            )}
          </div>

          {/* Footer with keyboard hints */}
          <div className="flex items-center gap-4 border-t px-4 py-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1">Enter</kbd> to select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border bg-muted px-1">Esc</kbd> to close
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
